import { useState } from 'react';
import Button from 'react-bootstrap/Button';
import { IoBuild } from 'react-icons/io5';

import { useProject } from '@contexts/Project.context';
import { useMapToolBusy } from '@hooks/useMapToolBusy';
import { useAuth } from '@contexts/User.context';

import { getGridFeatures } from '@utils/api';
import { setLayersVisibilityByCustomIds } from '@utils/map/helpers';
import AnnotateSingleImages from '../ai/AnnotateSingleImages';
import { PROJECT_MODE } from '@utils/constants';
import { useToast } from '@contexts/Toast.context';

const { SINGLE_IMAGE } = PROJECT_MODE;

const annotationSidebarId = 'annotationSidebar';
const EditDetections = () => {
	const { addToast } = useToast();
	const {
		selectedSingleImage,
		pickedTask,
		mapObject,
		project,
		setActiveSidebar,
		projectMode,
		annotationMode,
		detections,
		dispatch,
	} = useProject();

	const { busy: mapBusy, reason: blockedReason, getBusy } = useMapToolBusy();

	const { uuid: project_uuid } = project ?? {};
	const { model_uuid } = pickedTask ?? {};
	const { name } = selectedSingleImage?.getProperties() ?? {};
	const projectHasDetections = detections.length > 0;

	const {
		tierTrial,
		trialEnded,
		tierPro,
		tierPremium,
		subscriptionActive,
		isEnterprise,
		userAccess,
		activeOrgAccess,
	} = useAuth();

	const layersToHide = [
		'aoiLayer',
		'dsmLayer',
		'hexagonLayer',
		'trainingDataLayer',
		'centerLayer',
		'outlineLayer',
		'detailsLayer',
	];

	const [layersToShow, setLayersToShow] = useState([]);

	const getLayersToShow = () => {
		const layers = mapObject.getLayers()?.getArray();
		if (!layers?.length) return [];

		// Gets an array of customLayerId strings that are visible
		return layers.reduce((acc, layer) => {
			const { customLayerId, visible } = layer?.getProperties() ?? {};
			if (customLayerId && visible) acc.push(customLayerId);
			return acc;
		}, []);
	};

	const checkPermissions = async () => {
		const showAlert = msg => {
			addToast({
				id: 'annotation-permission-denied',
				bg: 'warning',
				title: msg,
			});
			return false;
		};

		if (tierTrial) return showAlert('Trial users cannot add or remove detections');

		if (tierPremium && !userAccess?.includes('train'))
			return showAlert('Your user does not have access rights to train AI');

		if (tierPro && !subscriptionActive)
			return showAlert('Your user needs an active subscription to add or remove detections');

		if (isEnterprise) {
			// Check if activeOrgAccess array contains "all" or "train"
			const hasOrgAccess =
				activeOrgAccess.includes('all') || activeOrgAccess.includes('train');

			if (!hasOrgAccess)
				return showAlert('Your user does not have access rights to train AI');
		}

		return true; // Allow training if none of the conditions are met
	};

	const startAnnotation = gridFeatures => {
		const taskLabels = pickedTask.classes.map(task => task.description);
		const taskIds = Array.from(Array(taskLabels.length).keys());
		const viewExtent = mapObject?.getView()?.calculateExtent(mapObject?.getSize());

		setLayersToShow(getLayersToShow());
		dispatch({
			type: 'setSavedMapExtent',
			payload: viewExtent,
		});
		dispatch({
			type: 'setAnnotationMode',
			payload: {
				gridFeatures,
			},
		});
		dispatch({
			type: 'setAnnotationSidebarData',
			payload: {
				classIds: taskIds,
				labels: taskLabels,
				activeClassId: taskIds[0],
			},
		});

		setActiveSidebar({ sidebarId: annotationSidebarId });
	};

	const attemptStartAnnotation = async () => {
		const isAnnotationAllowed = await checkPermissions();
		if (!isAnnotationAllowed) return;

		const grid = await getGridFeatures({ project_uuid, model_uuid });
		if (!grid || grid.length === 0)
			return console.error('Could not fetch grid for annotation.');

		const selectedGridFeature = grid.find(image => image?.properties?.tile_name === name);

		if (!selectedGridFeature) {
			addToast({
				id: 'start-annotation-error',
				bg: 'danger',
				title: 'An error occurred and annotation could not be started. Please try again later.',
			});
			return;
		}
		// The button was enabled when this was clicked, but both awaits above gave a
		// boundary edit or an AOI draw time to take the map over. Everything below
		// takes it over in turn, so ownership has to be asked again here.
		const { busy, reason } = getBusy();
		if (busy) {
			addToast({
				id: 'annotation-map-busy',
				bg: 'warning',
				title: reason,
			});
			return;
		}

		// Hide all layers that should not be visible during annotation
		setLayersVisibilityByCustomIds(mapObject, layersToHide, false);

		dispatch({
			type: 'setToolBarVisible',
			payload: false,
		});

		startAnnotation([selectedGridFeature]);
	};

	if (!selectedSingleImage) return null;
	if (!!annotationMode && projectMode == SINGLE_IMAGE)
		return <AnnotateSingleImages layersToShow={layersToShow} isDSPMode={true} />;

	// Starting annotation hides layers and takes the map over, which would land
	// on top of a boundary edit's Modify interaction or a live AOI draw — so
	// whatever owns the map has to be resolved first.
	const tooltipContent =
		blockedReason ||
		'Add/Remove detections to recalculate Digital Sample Plot and modify training data';

	return (
		<Button
			disabled={trialEnded || !projectHasDetections || mapBusy}
			onClick={attemptStartAnnotation}
			variant="dark-secondary"
			className="btn-dark secondary"
			data-tooltip-id="tooltip-toolbar-root"
			data-tooltip-content={tooltipContent}
			data-tooltip-place="top">
			<IoBuild className="m-1 mb-2" />
			Add/Remove Detections
		</Button>
	);
};

export default EditDetections;
